import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';

export default class extends Controller {
  static scriptPromise = null;

  static values = {
    createUrl: String,
    redirectUrl: String,
    fallbackUrl: String,
    widgetId: String,
    scriptUrl: String,
    lang: String,
  };

  static targets = ['loader'];

  connect() {
    this.abortController = new AbortController();
    this.fetchOnboardingUrl();
  }

  disconnect() {
    this.abortController?.abort();

    try {
      LemonwayWidget.destroy(this.widgetIdValue);
    } catch (_e) {
      // Widget may not be initialized yet
    }
  }

  async fetchOnboardingUrl() {
    try {
      const csrfToken = document.querySelector(
        'meta[name="csrf-token"]',
      )?.content;

      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        signal: this.abortController.signal,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        this.redirectWithError(
          data.error ? 'workflow' : 'generic',
        );
        return;
      }

      const data = await response.json();

      if (data.already_validated) {
        Turbo.visit(
          this.fallbackUrlValue, {action: 'replace'},
        );
        return;
      }

      this.onboardingUrl = data.url;
      await this.loadScript();
      this.initWidget();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.redirectWithError('generic');
    }
  }

  loadScript() {
    if (typeof LemonwayWidget !== 'undefined') {
      return Promise.resolve();
    }

    if (this.constructor.scriptPromise) {
      return this.constructor.scriptPromise;
    }

    this.constructor.scriptPromise = new Promise(
      (resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.scriptUrlValue;
        script.onload = resolve;
        script.onerror = () => {
          this.constructor.scriptPromise = null;
          reject(
            new Error('Failed to load Lemonway widget'),
          );
        };
        document.head.appendChild(script);
      },
    );

    return this.constructor.scriptPromise;
  }

  initWidget() {
    this.hideLoader();

    LemonwayWidget.init(
      this.onboardingUrl,
      this.widgetIdValue,
      {
        lang: this.langValue,
        arePersonalInformationsReadonly: true,
        onMyAccount: () => this.redirectToNextStep(false),
        onFinish: () => this.redirectToNextStep(true),
        onPageChange: () => {
          this.scrollContainer?.scrollTo({top: 0});
        },
      },
    );
  }

  get scrollContainer() {
    return this.element.closest('.overflow-auto');
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add('d-none');
    }
  }

  redirectWithError(code) {
    const url = new URL(
      this.fallbackUrlValue,
      window.location.origin,
    );
    url.searchParams.set('onboarding_error', code);
    Turbo.visit(url.toString(), {action: 'replace'});
  }

  redirectToNextStep(success = false) {
    const url = new URL(this.redirectUrlValue);

    if (success) {
      url.searchParams.set('status', 'success');
    }

    Turbo.visit(url.toString(), {action: 'replace'});
  }
}
